import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Cropper from 'react-cropper'

import { Box, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import DeleteIcon from '@mui/icons-material/Delete'
import CollectionsIcon from '@mui/icons-material/Collections'
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import MediaCropperComponent from '@professocial/professocialFormComponents/FormComponents/MediaCropperComponent'

import { MediaFileMetadata } from '@3m5/sm_publish-frontend-api'
import MediaGalleryComponent from '@professocial/professocialFormComponents/FormComponents/MediaGallery/MediaGalleryComponent'
import LightBox from '@professocial/professocialFormComponents/FormComponents/LightBox'
import MediaCarousel from '@professocial/professocialFormComponents/FormComponents/MediaCarousel'

type Props = {
  width?: string
  height?: string
  src?: string
  aspectRatio?: number
  mediaData?: MediaFileMetadata[]
  pictureType: string
  uploadImg?: MediaFileMetadata | undefined
  getMediaData?: (params: any) => void
  cropperOptions?: Cropper.GetCroppedCanvasOptions
  updateImage: (img: MediaFileMetadata | undefined) => void
  setUploadImg: (img: MediaFileMetadata | undefined) => void
  useRoundImageBorders?: boolean
}

interface CarouselMediaFile {
  mediaFileId: string
  directLink: string | undefined
}

const FacebookMediaUploadComponent: React.FC<Props> = (props) => {
  const [cropper, setCropper] = useState<any>()
  const [image, setImage] = useState<string | undefined>()
  const [selectedMedia, setSelectedMedia] = useState<MediaFileMetadata>()
  const [isOpen, setIsOpen] = useState(false)
  const [carouselImages, setCarouselImages] = useState<CarouselMediaFile[]>([])
  const [index, setIndex] = useState<number>(0)
  const [isUploadImg, setIsUploadImg] = useState(false)

  const useStyles = makeStyles((theme: Theme) => ({
    imageContainer: {
      position: 'relative',
      maxWidth: props.width ? props.width : '180px',
      width: '100%',
    },
    image: {
      objectFit: 'cover',
      position: 'relative',
      height: props.height ? props.height : '180px',
      maxWidth: props.width ? props.width : '180px',
      width: '100%',
    },
    iconContainer: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      height: 'auto',
      maxWidth: 'auto',
      width: '100%',
      padding: theme.spacing(0.8),
      bottom: 0,
      left: 0,
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '30px',
      width: '30px',
      cursor: 'pointer',
      boxShadow: '0px 0px 3px rgba(0, 0, 0, .15)',
      margin: theme.spacing(0.5),
      backgroundColor: theme.palette.common.white,
      borderRadius: '50%',
    },
  }))

  const { t } = useTranslation()
  const classes = useStyles()

  const handleCropped = (instance: Cropper) => {
    setCropper(instance)
  }

  const getCropData = () => {
    if (typeof cropper !== 'undefined') {
      const img = cropper.getCroppedCanvas(props.cropperOptions).toDataURL('image/jpeg')
      fetch(img)
        .then(res => res.blob())
        .then(blob => props.getMediaData && props.getMediaData({ file: blob }))
    }
    setIsOpen(!isOpen)
  }

  const onChangeImage = (img: string | undefined, file: File | undefined) => {
    setImage(img)
    setSelectedMedia(undefined)
  }

  const onChangeCarouselImage = (index: number) => {
    const indexMediaData = carouselImages && props.mediaData?.findIndex(item => item.mediaFileId === carouselImages[index].mediaFileId)
    if (props.mediaData && indexMediaData !== undefined && indexMediaData !== -1) {
      props.updateImage(props.mediaData[indexMediaData])
      setIsUploadImg(false)
    } else if (props.uploadImg) {
      props.updateImage(props.uploadImg)
      setIsUploadImg(true)
    } else {
      props.updateImage(undefined)
    }
  }

  const deleteImage = () => {
    setImage(undefined)
    const newArray = carouselImages && carouselImages.map((item, i) => i === 0
      ? { mediaFileId: 'current', directLink: props.src }
      : item)
    setCarouselImages(newArray)
    props.setUploadImg(undefined)
    props.updateImage(undefined)
  }

  const handleSelectedMedia = (item: any) => {
    setSelectedMedia(item)
  }

  const selectMediaFile = () => {
    if (selectedMedia) {
      const index = carouselImages?.findIndex(item => item.mediaFileId === selectedMedia.mediaFileId)
      if (index && index !== -1) {
        setIndex(index)
        setIsOpen(!isOpen)
        setSelectedMedia(undefined)
        props.updateImage(selectedMedia)
      }
    }
  }

  useEffect(() => {
    const mappedImages: CarouselMediaFile[] | undefined = []
    if (props.uploadImg) {
      mappedImages.push({ mediaFileId: props.uploadImg.mediaFileId, directLink: props.uploadImg.directLink })
      props.updateImage(props.uploadImg)
      setIsUploadImg(true)
    } else {
      mappedImages.push({ mediaFileId: 'current', directLink: props.src })
    }
    props.mediaData && mappedImages.push(...props.mediaData.map(({ mediaFileId, directLink }) => ({ mediaFileId, directLink })))
    mappedImages && setCarouselImages(mappedImages)
  }, [props.mediaData, props.uploadImg, props.src])

  return (
    <div className={`${classes.imageContainer}`}>
      {carouselImages && props.height && props.width && (
        <MediaCarousel
          parentIndex={index}
          items={carouselImages.map(item => item.directLink)}
          height={props.height}
          maxWidth={props.width}
          onChange={onChangeCarouselImage}
          navButtonsAlwaysVisible={false}
          navDotsVisible={false}
        />
      )}
      <div className={classes.iconContainer}>
        {props.uploadImg && isUploadImg && (
          <div className={classes.icon} style={{ marginRight: 'auto' }} onClick={deleteImage}>
            <DeleteIcon fontSize='small' color='primary' />
          </div>
        )}
        <LightBox
          isOpenParent={isOpen}
          setOpenParent={setIsOpen}
          payload={
            <MediaCropperComponent
              mediaFile={image}
              description={t('facebookSettingsCommon:uploadImageHint')}
              initialAspectRatio={props.aspectRatio}
              aspectRatio={props.aspectRatio}
              getCropData={getCropData}
              handleCropped={handleCropped}
              onChangeImage={onChangeImage}
            />
          }
        >
          <div className={classes.icon}>
            <AddToPhotosIcon fontSize='small' color='primary' />
          </div>
        </LightBox>

        <LightBox
          isOpenParent={isOpen}
          setOpenParent={setIsOpen}
          payload={
            <>
              {props.mediaData && (
                <MediaGalleryComponent
                  mediaData={props.mediaData}
                  setSelected={handleSelectedMedia}
                  selected={selectedMedia}
                  useRoundImageBorders={props.useRoundImageBorders}
                  wideView={props.pictureType === 'cover'}
                />
              )}
              <Box component='div' style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', marginTop: '10px' }}>
                <ButtonComponent
                  onClick={selectMediaFile}
                  text={t('facebookSettingsCommon:useImage')}
                />
              </Box>
            </>
          }
        >
          <div className={classes.icon}>
            <CollectionsIcon fontSize='small' color='primary' />
          </div>
        </LightBox>
      </div>
    </div>
  )
}
export default FacebookMediaUploadComponent
